import { onTimeout } from "@suse-o11y/on-timeout";
import React, { useEffect, useRef, useState } from "react";

// TODO(anton): Discuss migrating Markdown to src/components/markdown/ — reuses react-markdown stack
import { Markdown } from "../../components/react/markdown/Markdown";

import { ExpandableContent } from "./_expandable-content";
import type { TextPartType } from "./_part-types";
import { SeverityBadge } from "./_severity-badge";
import { ChatBubble } from "./chat-bubble";

const DEBOUNCE_MS = 40;

/**
 * Debounces a rapidly changing string value to prevent the markdown parser
 * from thrashing on every streaming character delta.
 */
function useDebouncedText(text: string): string {
	const [debounced, setDebounced] = useState(text);
	const prevRef = useRef(text);

	useEffect(() => {
		// If the text stopped changing (same as debounced), skip the timer
		if (text === debounced) {
			return;
		}

		return onTimeout(() => {
			setDebounced(text);
		}, DEBOUNCE_MS);
	}, [text, debounced]);

	// If the text got shorter — new message, reset immediately
	useEffect(() => {
		if (text.length < prevRef.current.length) {
			setDebounced(text);
		}
		prevRef.current = text;
	}, [text]);

	return debounced;
}

const joinTextParts = (parts: ReadonlyArray<TextPartType>): TextPartType => {
	const [first, ...rest] = parts;

	if (first == null) {
		return { type: "text", content: "" };
	}

	return {
		...first,
		content: [first.content ?? "", ...rest.map((part) => part.content ?? "")].join(""),
	};
};

export const TextPart: React.FC<{ part?: TextPartType; parts?: ReadonlyArray<TextPartType> }> = ({ part, parts }) => {
	const mergedPart = parts == null ? part : joinTextParts(parts);
	const content = useDebouncedText(mergedPart?.content ?? "");
	const severity = mergedPart?.severity;

	return (
		<ChatBubble copyable copyText={mergedPart?.content ?? ""} variant="system">
			{severity && <SeverityBadge severity={severity} />}
			<ExpandableContent>
				<div className="text-sm">
					<Markdown copyCodeToClipboard openLinksInNewTab sanitize text={content} />
				</div>
			</ExpandableContent>
		</ChatBubble>
	);
};
